"use client";

import type { DialogContentProps } from "@radix-ui/react-dialog";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback } from "react";
import { Button } from "./button";
import { useKeyboardShortcut } from "./hooks/use-global-shortcut";
import { Icon } from "./icon";
import { cn } from "./lib/utils";

interface ModalProps {
	children: React.ReactNode;
	open?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	escapeLayer?: number;
}

export function Modal({
	children,
	open,
	onOpenChange,
	escapeLayer = 2,
}: ModalProps): React.ReactElement {
	const handleEscape = useCallback(() => {
		onOpenChange?.(false);
	}, [onOpenChange]);

	useKeyboardShortcut("Escape", handleEscape, !open, escapeLayer);

	return (
		<Dialog.Root onOpenChange={onOpenChange} open={open} modal>
			<AnimatePresence mode="wait">{children}</AnimatePresence>
		</Dialog.Root>
	);
}

export const Trigger = Dialog.Trigger;

export interface ModalContentProps extends Partial<DialogContentProps> {
	children: React.ReactNode;
	className?: string;
	innerClassName?: string;
	overlayClassName?: string;
	closeButton?: boolean;
	transparent?: boolean;
	title?: string;
	description?: string;
}

export function Content({
	children,
	className,
	innerClassName,
	transparent = false,
	closeButton = true,
	title = "Dialog",
	description,
	...props
}: ModalContentProps): React.ReactElement {
	return (
		<Dialog.Portal>
			<Dialog.Overlay
				asChild
				className="fixed top-0 right-0 bottom-0 left-0 z-30 h-full w-full bg-[rgba(0,0,0,0.24)]"
			>
				<motion.div
					animate={{ opacity: 1 }}
					initial={{ opacity: 0 }}
					exit={{ opacity: 0 }}
					className="h-full w-full backdrop-blur-lg"
				/>
			</Dialog.Overlay>
			<Dialog.Content
				className={cn(
					"-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-40 outline-none",
					className,
				)}
				onCloseAutoFocus={(event) => {
					event.preventDefault();
				}}
				onEscapeKeyDown={(event) => {
					event.preventDefault();
				}}
				{...props}
			>
				<Dialog.Title className="sr-only">{title}</Dialog.Title>
				{description && (
					<Dialog.Description className="sr-only">
						{description}
					</Dialog.Description>
				)}
				<motion.div
					animate={{ opacity: 1, scale: 1 }}
					className={cn(
						"h-full w-full",
						"relative",
						!transparent && "overflow-hidden bg-surface",
						innerClassName,
					)}
					initial={{ opacity: 0, scale: 1.05 }}
					exit={{ opacity: 0, scale: 0.9 }}
					transition={{ duration: 0.2 }}
				>
					{children}
					{closeButton ? (
						<Dialog.Close asChild>
							<Button
								className="absolute top-md right-md text-text-icon outline-none"
								size="icon"
							>
								<Icon kind="close" />
							</Button>
						</Dialog.Close>
					) : null}
				</motion.div>
			</Dialog.Content>
		</Dialog.Portal>
	);
}

export function ModalContent({
	children,
	className,
	overlayClassName,
	title = "Dialog",
	description,
}: ModalContentProps): React.ReactElement {
	return (
		<Dialog.Portal>
			<Dialog.Overlay asChild className={overlayClassName}>
				<motion.div
					animate={{ opacity: 1 }}
					initial={{ opacity: 0 }}
					exit={{ opacity: 0 }}
				/>
			</Dialog.Overlay>
			<Dialog.Content
				className={cn(className)}
				aria-description="modal dialog window"
			>
				<Dialog.Title className="overfloww-hidden h-0 w-0 opacity-0">
					{title}
				</Dialog.Title>
				<Dialog.Description className="overfloww-hidden h-0 w-0 opacity-0">
					{description}
				</Dialog.Description>
				<motion.div
					className="h-full w-full"
					animate={{ opacity: 1, scale: 1 }}
					initial={{ opacity: 0, scale: 1.05 }}
					exit={{ opacity: 0, scale: 0.95 }}
					transition={{ duration: 0.2 }}
				>
					{children}
				</motion.div>
			</Dialog.Content>
		</Dialog.Portal>
	);
}

export const Close = Dialog.Close;
